import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/app/AppLayout";
import { plansApi, auth } from "../lib/api";
import { invalidatePlanCache } from "../components/app/PlanLadder";
import {
  Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, Info, RotateCcw,
} from "lucide-react";
import { getApiError } from "../lib/utils";

const TID_PREFIX = "plans-config";

export default function Plans() {
  const user = auth.me();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]); // [{name, active}]
  const [original, setOriginal] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await plansApi.getConfig();
      const rows = r.plans.map((p) => ({ name: p.name, active: p.active }));
      setPlans(rows);
      setOriginal(rows);
      setUpdatedAt(r.updated_at);
    } catch (e) {
      toast.error("Failed to load plan config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(plans) !== JSON.stringify(original);

  const move = (idx, dir) => {
    setPlans((p) => {
      const n = [...p];
      const j = idx + dir;
      if (j < 0 || j >= n.length) return n;
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  };
  const rename = (idx, value) => {
    const clean = value.toUpperCase().replace(/[^A-Z0-9 &\-]/g, "").slice(0, 40);
    setPlans((p) => p.map((x, i) => (i === idx ? { ...x, name: clean } : x)));
  };
  const toggleActive = (idx) => setPlans((p) => p.map((x, i) => (i === idx ? { ...x, active: !x.active } : x)));
  const remove = (idx) => setPlans((p) => p.filter((_, i) => i !== idx));
  const add = () => {
    const clean = newName.trim().toUpperCase().replace(/[^A-Z0-9 &\-]/g, "").slice(0, 40);
    if (!clean) return;
    if (plans.some((p) => p.name === clean)) {
      toast.error(`${clean} already exists`);
      return;
    }
    setPlans((p) => [...p, { name: clean, active: true }]);
    setNewName("");
  };

  const save = async () => {
    if (plans.length === 0) {
      toast.error("At least one plan is required");
      return;
    }
    setSaving(true);
    try {
      const r = await plansApi.putConfig(plans);
      const rows = r.plans.map((p) => ({ name: p.name, active: p.active }));
      setPlans(rows);
      setOriginal(rows);
      setUpdatedAt(r.updated_at);
      invalidatePlanCache();
      toast.success("Plan order saved. Facility views will refresh.");
    } catch (e) {
      toast.error(getApiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const reset = () => setPlans(original);

  if (!isSuperAdmin) {
    return (
      <AppLayout>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Plan configuration is limited to Super Administrators.
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">Configuration</div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 md:text-3xl">Plans</h1>
        </div>

        <div className="mb-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
          <Info size={14} className="mt-0.5 shrink-0 text-nmi-700" />
          <div>
            Order top-to-bottom = lowest tier → highest tier. Renaming here doesn't rewrite existing facility data —
            it only changes what the filter and the ladder call each tier. Keep names consistent with the values stored in <code>nmi_plans</code>.
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-12 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">#</th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Plan name</th>
                  <th className="w-24 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Active</th>
                  <th className="w-44 px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p, i) => (
                  <tr
                    key={i}
                    data-testid={`${TID_PREFIX}-row-${i}`}
                    className={`border-t border-slate-100 ${p.active ? "" : "bg-slate-50 opacity-60"}`}
                  >
                    <td className="px-2 py-2 font-mono text-xs text-slate-500">{i + 1}</td>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`${TID_PREFIX}-name-${i}`}
                        value={p.name}
                        onChange={(e) => rename(i, e.target.value)}
                        className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 font-mono text-sm font-bold uppercase tracking-wider text-slate-900 focus:border-nmi-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-nmi-600/20"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs">
                        <input
                          data-testid={`${TID_PREFIX}-active-${i}`}
                          type="checkbox"
                          checked={p.active}
                          onChange={() => toggleActive(i)}
                        />
                        {p.active ? "Active" : "Hidden"}
                      </label>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`${TID_PREFIX}-up-${i}`}
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                          className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                          title="Move up"
                        ><ArrowUp size={14} /></button>
                        <button
                          data-testid={`${TID_PREFIX}-down-${i}`}
                          disabled={i === plans.length - 1}
                          onClick={() => move(i, +1)}
                          className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                          title="Move down"
                        ><ArrowDown size={14} /></button>
                        <button
                          data-testid={`${TID_PREFIX}-remove-${i}`}
                          onClick={() => {
                            if (window.confirm(`Remove "${p.name}" from the plan list? Facilities still storing this token will just show it greyed out until you rename or delete it here.`)) {
                              remove(i);
                            }
                          }}
                          className="rounded-md p-1 text-red-600 hover:bg-red-50"
                          title="Remove"
                        ><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 p-3">
              <input
                data-testid={`${TID_PREFIX}-new-name`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                placeholder="Add a new plan (e.g. ULTIMATE)"
                className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
              />
              <button
                data-testid={`${TID_PREFIX}-add`}
                onClick={add}
                disabled={!newName.trim()}
                className="inline-flex items-center gap-1.5 rounded-md border border-nmi-700 bg-white px-3 py-2 text-xs font-semibold text-nmi-800 hover:bg-nmi-50 disabled:opacity-50"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <div>
            {updatedAt ? <>Last updated: {new Date(updatedAt).toLocaleString()}</> : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={reset}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RotateCcw size={12} /> Revert
            </button>
            <button
              data-testid={`${TID_PREFIX}-save`}
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-4 py-2 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={2.5} />}
              Save order
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
